import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/formatDate";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, HelpCircle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const DELIVERY_STATUS = {
  ok: { label: "Lieferung geprüft", cls: "border-emerald-300 bg-emerald-50 text-emerald-800", Icon: CheckCircle2 },
  deviation: { label: "Abweichung Lieferschein", cls: "border-amber-300 bg-amber-50 text-amber-800", Icon: AlertTriangle },
  unverifiable: { label: "Lieferschein nicht prüfbar", cls: "border-slate-300 bg-slate-50 text-slate-600", Icon: HelpCircle },
};

const ROW_STATUS = {
  ok: { label: "OK", cls: "text-emerald-700" },
  qty_mismatch: { label: "Menge abweichend", cls: "text-amber-700 font-medium" },
  invoice_only: { label: "Nur auf Rechnung", cls: "text-rose-700" },
  delivery_only: { label: "Nur auf Lieferschein", cls: "text-sky-700" },
};

const fmtQty = (q) => (q == null ? "—" : Number(q) % 1 === 0 ? String(q) : Number(q).toFixed(2));

export function DeliveryCheckBadge({ bill, onClick }) {
  const st = bill.delivery_check?.status;
  const hasNote = (bill.attachments || []).some((a) => a.kind === "delivery_note");
  if (!st && !hasNote) return null;
  const meta = DELIVERY_STATUS[st] || DELIVERY_STATUS.unverifiable;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] ${meta.cls} hover:opacity-80`}
      title={bill.delivery_check?.message || "Lieferschein-Abgleich anzeigen"}
      data-testid={`bill-delivery-badge-${bill._id}`}
    >
      <meta.Icon className="h-3 w-3" /> {meta.label}
    </button>
  );
}

export function DeliveryCheckPanel({ bill, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const dc = bill.delivery_check;
  const rerun = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/bills/${bill._id}/delivery-check`);
      onUpdated({ ...bill, delivery_check: r.data });
      toast.success(r.data.message || "Abgleich ausgeführt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abgleich fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  const meta = DELIVERY_STATUS[dc?.status] || DELIVERY_STATUS.unverifiable;
  return (
    <div className="space-y-3" data-testid="bill-delivery-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${meta.cls}`} data-testid="bill-delivery-status">
          <meta.Icon className="h-4 w-4" />
          <span className="font-medium">{meta.label}</span>
          {dc?.message && <span className="opacity-80">· {dc.message}</span>}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          {dc?.checked_at && <span>Geprüft {formatDateTime(dc.checked_at)}</span>}
          <Button type="button" size="sm" variant="outline" onClick={rerun} disabled={busy} data-testid="bill-delivery-rerun">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Neu prüfen
          </Button>
        </div>
      </div>
      {dc?.items?.length > 0 ? (
        <div className="overflow-auto max-h-[60vh] rounded border">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-2 py-1.5 font-medium">Position Rechnung</th>
                <th className="px-2 py-1.5 font-medium text-right">Verrechnet</th>
                <th className="px-2 py-1.5 font-medium">Position Lieferschein</th>
                <th className="px-2 py-1.5 font-medium text-right">Geliefert</th>
                <th className="px-2 py-1.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {dc.items.map((it, i) => {
                const rs = ROW_STATUS[it.status] || ROW_STATUS.ok;
                return (
                  <tr key={i} className={`border-t ${it.status !== "ok" ? "bg-amber-50/40" : ""}`} data-testid={`bill-delivery-row-${i}`}>
                    <td className="px-2 py-1.5">{it.invoice_desc || <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtQty(it.qty_invoice)}{it.unit && it.qty_invoice != null ? ` ${it.unit}` : ""}</td>
                    <td className="px-2 py-1.5">{it.delivery_desc || <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtQty(it.qty_delivered)}{it.unit && it.qty_delivered != null ? ` ${it.unit}` : ""}</td>
                    <td className={`px-2 py-1.5 ${rs.cls}`}>{rs.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-slate-500">
          {dc ? "Keine Positionen verglichen." : "Noch kein Abgleich ausgeführt – «Neu prüfen» liest Positionen aus Rechnung und Lieferschein per KI aus."}
        </p>
      )}
    </div>
  );
}
